/**
 * セッション整合性用: GET /api/me を no-store で取得し、認証済みかどうかを返す。
 * 同一タスク内の並行呼び出しは 1 リクエストにまとめる。
 */
import { shallowRef } from 'vue';

/** 共通レイアウト（`AuthenticationLayout.vue`）のルート要素 */
export const AUTH_LAYOUT_ROOT_SELECTOR = '[data-auth-layout-root]';

/** グローバルヘッダーと本文の余白クラス（`#app` ではなくレイアウトルート）の単一ソース */
export const appAuthPresentation = shallowRef<boolean | null>(null);

/** 初回: ヘッダー・本文マウント完了まで Vue 側の待機レイヤーを表示する */
export const authShellRevealed = shallowRef(false);

let authShellPendingCycleActive = false;

type AuthShellPart = 'header' | 'content';

type AuthShellState = {
  initialAuthResolved: boolean;
  authenticated: boolean;
  initialAuthPromise: Promise<boolean> | null;
  partsReady: Set<AuthShellPart>;
  fallbackTimerId: number | null;
};

const AUTH_SHELL_FALLBACK_TIMEOUT_MS = 4000;

declare global {
  interface Window {
    __aimfAuthShellState__?: AuthShellState;
  }
}

let inFlight: Promise<boolean> | null = null;
let inFlightForce = false;
/** `fetchMeIsAuthenticated({ force: true })` 時に古い in-flight の finally で inFlight を壊さないための世代 */
let meAuthFetchSeq = 0;

/** `force` なしの直近成功結果（ルーター遷移ごとの /api/me 連打を抑える） */
const ME_AUTH_TTL_MS = 30_000;
let meAuthCached: { value: boolean; at: number } | null = null;

export function invalidateMeAuthCache(): void {
  meAuthCached = null;
  inFlight = null;
  inFlightForce = false;
  meAuthFetchSeq += 1;
}

const getAuthShellState = (): AuthShellState => {
  if (!window.__aimfAuthShellState__) {
    window.__aimfAuthShellState__ = {
      initialAuthResolved: false,
      authenticated: false,
      initialAuthPromise: null,
      partsReady: new Set<AuthShellPart>(),
      fallbackTimerId: null,
    };
  }

  return window.__aimfAuthShellState__;
};

const clearFallbackTimer = (state: AuthShellState): void => {
  if (state.fallbackTimerId === null) return;
  window.clearTimeout(state.fallbackTimerId);
  state.fallbackTimerId = null;
};

const revealAuthShell = (): void => {
  authShellPendingCycleActive = false;
  authShellRevealed.value = true;
};

const scheduleFallbackReveal = (state: AuthShellState): void => {
  clearFallbackTimer(state);
  state.fallbackTimerId = window.setTimeout(() => {
    revealAuthShell();
    state.fallbackTimerId = null;
  }, AUTH_SHELL_FALLBACK_TIMEOUT_MS);
};

export function markAuthShellPending(): void {
  if (typeof window === 'undefined') return;
  if (!document.getElementById('global-header-vue') || !document.getElementById('app')) return;

  const state = getAuthShellState();
  if (!authShellPendingCycleActive) {
    state.partsReady.clear();
    authShellPendingCycleActive = true;
  }
  authShellRevealed.value = false;
  scheduleFallbackReveal(state);
}

export function markAuthShellPartReady(part: AuthShellPart): void {
  if (typeof window === 'undefined') return;
  const state = getAuthShellState();
  state.partsReady.add(part);

  if (state.partsReady.has('header') && state.partsReady.has('content')) {
    clearFallbackTimer(state);
    revealAuthShell();
  }
}

export async function ensureInitialAuthResolved(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const state = getAuthShellState();
  if (state.initialAuthResolved) return state.authenticated;
  if (state.initialAuthPromise) return state.initialAuthPromise;

  state.initialAuthPromise = (async () => {
    const authenticated = await fetchMeIsAuthenticated();
    state.initialAuthResolved = true;
    state.authenticated = authenticated;
    syncAppAuthClass(authenticated);
    return authenticated;
  })().finally(() => {
    state.initialAuthPromise = null;
  });

  return state.initialAuthPromise;
}

export async function fetchMeIsAuthenticated(options?: { force?: boolean }): Promise<boolean> {
  if (!options?.force && meAuthCached && Date.now() - meAuthCached.at < ME_AUTH_TTL_MS) {
    return meAuthCached.value;
  }

  if (!options?.force && inFlight) {
    return inFlight;
  }
  if (options?.force && inFlight && inFlightForce) {
    return inFlight;
  }

  const seq = ++meAuthFetchSeq;
  const setCacheIfLatest = (value: boolean): boolean => {
    if (seq === meAuthFetchSeq) {
      meAuthCached = { value, at: Date.now() };
    }
    return value;
  };
  const pending = (async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/me', {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) return setCacheIfLatest(false);

      const data = (await response.json().catch(() => ({}))) as { authenticated?: boolean };
      const ok = data?.authenticated === true;
      return setCacheIfLatest(ok);
    } catch {
      return setCacheIfLatest(false);
    }
  })();

  inFlight = pending;
  inFlightForce = Boolean(options?.force);
  void pending.finally(() => {
    if (seq === meAuthFetchSeq) {
      inFlight = null;
      inFlightForce = false;
    }
  });

  return pending;
}

export function syncAppAuthClass(authenticated: boolean): void {
  appAuthPresentation.value = authenticated;
}
